import camelcaseKeys from "camelcase-keys";
import { AxiosError, AxiosResponse } from "axios";
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { AXIOS } from "hooks/axios";
import { useError } from "components/status/ErrorProvider";
import { useSuccess } from "components/status/SuccessProvider";

interface RequestFullSchema {
  id: string;
  text: string;
  status: "PENDING" | "APPROVED" | "REJECTED";
  comment: string;
  subject: string;
  project: string;
  approver: string;
  requester: string;
  updatedAt: string;
}

export const usePavmRequestDetail = (uri: string, id: string) => {
  return useQuery<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    RequestFullSchema
  >(
    [`PAVM REQUEST DETAIL ${uri} ${id}`],
    () => AXIOS.get(`${uri}/requests/${id}`),
    {
      retry: 2,
      enabled: !!uri && !!id,
      staleTime: 1000 * 120,
      select: (data) => camelcaseKeys(data.data, { deep: true }),
    }
  );
};

type RequestPostSchema = {
  id: string;
  uri: string;
  project: string;
  comment: string;
};

export type AxiosMutationRequest = UseMutationResult<
  AxiosResponse<any, any>,
  AxiosError<{ message: string }, any>,
  RequestPostSchema,
  unknown
>;

export const usePavmApprove = () => {
  const qc = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }, any>,
    RequestPostSchema
  >(
    [`PAVM REQUEST APPROVE`],
    ({ uri, id, project, comment }: RequestPostSchema) =>
      AXIOS.post(`${uri}/requests/approve`, { id, project, comment }),
    {
      onSuccess: async (data) => {
        setSuccess(`Request approved`);

        // refetch all active queries to ensure latest state
        qc.refetchQueries({ type: "active" });

        return camelcaseKeys(data.data, { deep: true });
      },
      onError: (error) => {
        const message = error.response?.data.message ?? "please try again";
        setError({ message, reset: mutation.reset });
      },
    }
  );

  return mutation;
};

export const usePavmReject = () => {
  const qc = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }, any>,
    RequestPostSchema
  >(
    [`PAVM REQUEST REJECT`],
    ({ uri, id, project, comment }: RequestPostSchema) =>
      AXIOS.post(`${uri}/requests/reject`, { id, project, comment }),
    {
      onSuccess: (data) => {
        setSuccess(`Request rejected`);

        // refetch all active queries to ensure latest state
        qc.refetchQueries({ type: "active" });

        return camelcaseKeys(data.data, { deep: true });
      },
      onError: (error) => {
        const message = error.response?.data.message ?? "please try again";
        setError({ message, reset: mutation.reset });
      },
    }
  );

  return mutation;
};
